import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PublicHeroCarousel from './PublicHeroCarousel';
import LandingPromoShowcase, { type LandingPromotionData } from './LandingPromoShowcase';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function isPromoActive(data: LandingPromotionData | null): data is LandingPromotionData {
  if (!data || !data.enabled) return false;
  const hasHeadline = Boolean(data.headlinePrimary?.trim());
  const hasImage = Boolean(data.imageUrl?.trim());
  return hasHeadline || hasImage;
}

/**
 * First screen: admin-configured landing promotion when enabled, otherwise hero carousel.
 * Renders the carousel immediately — never blocks the page on a network round-trip.
 */
export function HomeLandingHero() {
  const { i18n } = useTranslation();
  const [promo, setPromo] = useState<LandingPromotionData | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);

    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/settings/landing-promotion`,
            i18n.resolvedLanguage || i18n.language,
          ),
          { signal: controller.signal },
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as LandingPromotionData;
        if (!cancelled) setPromo(data);
      } catch {
        /* carousel fallback */
      } finally {
        window.clearTimeout(timeout);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  if (isPromoActive(promo)) {
    return <LandingPromoShowcase data={promo} />;
  }

  return <PublicHeroCarousel />;
}

export default HomeLandingHero;
